import { useCallback, useMemo, useState } from "react";

import { toastify } from "@vendors";
import { useModal, useConfigFile, useCurrentUrl } from "@hooks";
import {
  findFavoritedPageByPath,
  normalizeFavoritedPagePath,
  deleteFavoritedPage,
} from "@utils/favoritedPages";
import FormModal from "@components/modal/FormModal";
import ActionMenuBaseItem from "@components/action_menu/item_types/ActionMenuBaseItem";

const FAVORITE_MODAL_CONFIG_PATH = "resources/favorited-kinn-pages/_modal";

const getPathAndSearchFromUrl = (url) => {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    return `${parsedUrl.pathname}${parsedUrl.search}`;
  } catch {
    return `${window.location.pathname}${window.location.search}`;
  }
};

export default function ActionMenuFavoritedPageToggleItem(props) {
  const {
    pageKey,
    defaultLabel,
    defaultIcon,
    className,
    helpText,
    disabledText,
  } = props;

  const [modalConfig, isModalConfig] = useConfigFile(FAVORITE_MODAL_CONFIG_PATH, {});
  const modal = useModal();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track the live URL so the favorited state reacts to filter changes and matches on exact params.
  const { url: currentUrl } = useCurrentUrl();

  // Capture the live pathname and filters so favorites preserve the exact view (including detail page tabs).
  const currentPath = useMemo(() => {
    return normalizeFavoritedPagePath(getPathAndSearchFromUrl(currentUrl));
  }, [currentUrl]);

  const favoritedPage = findFavoritedPageByPath(currentPath);
  const isCurrentPageFavorited = Boolean(favoritedPage);

  const modalData = useMemo(() => ({
    path: currentPath,
    page_key: pageKey,
    description: defaultLabel,
    label_text: defaultLabel,
    label_icon: defaultIcon,
  }), [currentPath, pageKey, defaultLabel, defaultIcon]);

  const handleClick = useCallback((event) => {
    if (!favoritedPage) {
      if (isModalConfig) modal.open();
      return;
    }

    event.keepOpen = true;

    if (isSubmitting) return;

    const confirmationMessage = `Remove "${favoritedPage.label_text}" from Favorites?`;
    if (!window.confirm(confirmationMessage)) return;

    setIsSubmitting(true);

    deleteFavoritedPage(favoritedPage.id).then(() => {
      toastify.success(`Removed <b>${favoritedPage.label_text}</b> from Favorites!`);
      location.reload();
    }).catch((error) => {
      console.error(error);
      toastify.error("Something went wrong updating Favorites!");
      setIsSubmitting(false);
    });
  }, [favoritedPage, isSubmitting, isModalConfig, modal]);

  const label = isCurrentPageFavorited ? {
    text: "Remove Page from Favorites",
    icon: {
      name: "star",
      color: "#C29900",
    },
  } : {
    text: "Add Page to Favorites",
    icon: "far:star",
  };

  return (
    <>
      <ActionMenuBaseItem
        onClick={handleClick}
        isDisabled={isSubmitting}
        label={label}
        hotkeys={["alt+f"]}
        className={className}
        helpText={helpText}
        disabledText={disabledText}
      />
      <FormModal
        mx={modal}
        data={modalData}
        config={modalConfig}
      />
    </>
  );
}
